import { useMemo, useCallback } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { useAuth } from '@/auth/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { routes } from '@/utils/routes'
import type { CompanySummary } from '@/components/common/CompanySelector'
import type { ClientWorkspaceSummaryViewDto } from '@/types/company'

interface UseCompanyWorkspaceNavOptions {
  clientCompanyId?: string
  onWorkspaceChange?: (workspace: ClientWorkspaceSummaryViewDto) => void
}

export function useCompanyWorkspaceNav({ clientCompanyId, onWorkspaceChange }: UseCompanyWorkspaceNavOptions) {
  const { allWorkspaces } = useAuth()
  const location = useLocation()
  const { workspaces, currentWorkspaceId } = useCompany()

  // Resolve company ID from props or route
  const routeCompanyId = useMemo(() => {
    if (clientCompanyId) return clientCompanyId
    try {
      const match = location?.path?.match(/\/company\/([^/]+)/)
      return match?.[1]
    } catch {
      return undefined
    }
  }, [clientCompanyId, location?.path])

  // Build list of companies user has access to
  const companies = useMemo((): CompanySummary[] => {
    if (!allWorkspaces || allWorkspaces.length === 0) return []
    const companyMap = new Map<string, CompanySummary>()
    allWorkspaces.forEach(ws => {
      if (ws.ClientCompanyId && ws.ClientCompanyName && !companyMap.has(ws.ClientCompanyId)) {
        companyMap.set(ws.ClientCompanyId, { id: ws.ClientCompanyId, name: ws.ClientCompanyName })
      }
    })
    return Array.from(companyMap.values())
  }, [allWorkspaces])

  const showCompanySelector = companies.length > 1

  // Filter workspaces for the current company
  const currentCompanyWorkspaces = useMemo(() => {
    if (!allWorkspaces || !routeCompanyId) return workspaces
    return allWorkspaces.filter(ws => ws.ClientCompanyId === routeCompanyId)
  }, [allWorkspaces, routeCompanyId, workspaces])

  const handleCompanyChange = useCallback((companyId: string) => {
    location.route(routes.company(companyId))
  }, [location])

  const handleWorkspaceChange = useCallback((workspace: ClientWorkspaceSummaryViewDto) => {
    if (workspace.ClientCompanyId !== routeCompanyId) {
      location.route(routes.companyWorkspace(workspace.ClientCompanyId, workspace.Id))
    } else {
      onWorkspaceChange?.(workspace)
    }
  }, [routeCompanyId, onWorkspaceChange, location])

  return {
    routeCompanyId,
    companies,
    showCompanySelector,
    currentCompanyWorkspaces,
    currentWorkspaceId,
    handleCompanyChange,
    handleWorkspaceChange,
  }
}
